import { AxiosResponse } from 'axios';
import { mapKeys } from 'lodash-es';
import { create } from 'zustand';

import { SECURE_API } from 'src/api/api';
import { LanguageKeys } from 'src/common/constants/languages';
import { compareTechnicalTypesByDistanceKm } from 'src/helpers/statisticExtras';
import { ICompetition } from 'src/interfaces/Competition';
import { IEdition, normalizeEditions } from 'src/interfaces/Edition';
import { IRole } from 'src/interfaces/Role';
import { TechnicalType } from 'src/interfaces/TechnicalType';
import { createTechnicalTypeCrudAction } from './actions/TechnicalTypeCrudImpl';
import {
  ACCOMMODATIONS_CRUD_CONFIG,
  AccommodationSlice,
  COMPETITIONS_CRUD_CONFIG,
  CompetitionSlice,
  DISTANCES_CRUD_CONFIG,
  DistanceSlice,
} from './config/technicalTypesCrud';
import { flattenActions } from './lib/flattenActions';
import { createDedupedFetcher } from './lib/dedupePromise';
import { StoreGetter, StoreSetter } from './lib/types';
import { EditionSlice, createEditionsSlice } from './slices/createEditionsSlice';

type SystemOptionsStore = ISystemOptionsState &
  AccommodationSlice &
  DistanceSlice &
  EditionSlice &
  CompetitionSlice;

interface ISystemOptionsState {
  dateFormat: Record<LanguageKeys, string>;
  dateTimeFormat: Record<LanguageKeys, string>;
  roles: IRole[];
  rolesById?: { [key: string]: IRole };
  timeFormat: Record<LanguageKeys, string>;
  isLoading: boolean;
  fetchRoles: () => Promise<void | AxiosResponse<null>>;
  fetchSystemOptions: () => Promise<void | AxiosResponse<null>>;
  setDateTimeFormat: (args: {
    dateFormat: Record<LanguageKeys, string>;
    dateTimeFormat: Record<LanguageKeys, string>;
    timeFormat: Record<LanguageKeys, string>;
  }) => void;
}

const useSystemOptionsStore = create<SystemOptionsStore>()((...params) => {
  const [set, get] = params;

  const fetchSystemOptionsImpl = async () => {
    set({ isLoading: true });
    return SECURE_API.get<{
      activeEdition: {
        id: string;
        activeEditionId?: string;
      };
      accommodations: TechnicalType[];
      competitions: ICompetition[];
      distances: TechnicalType[];
      editions: IEdition[];
    }>('systemOptions/')
      .then(({ data: { activeEdition, accommodations, competitions, distances, editions } }) => {
        const normalizedEditions = normalizeEditions(editions);
        const sortedDistances = [...distances].sort(compareTechnicalTypesByDistanceKm);
        set({
          activeEditionId: activeEdition.activeEditionId,
          accommodations: accommodations,
          accommodationsById: mapKeys(accommodations, 'id'),
          accommodationsByName: mapKeys(accommodations, 'name'),
          competitions: competitions,
          competitionsById: mapKeys(competitions, 'id'),
          competitionsByName: mapKeys(competitions, 'name'),
          distances: sortedDistances,
          distancesById: mapKeys(sortedDistances, 'id'),
          distancesByName: mapKeys(sortedDistances, 'name'),
          editions: normalizedEditions,
          editionsById: mapKeys(normalizedEditions, 'id'),
          editionsByYear: mapKeys(normalizedEditions, 'year'),
          isLoading: false,
        });
      })
      .catch((error: string) => {
        set({
          activeEditionId: undefined,
          accommodations: [],
          accommodationsById: undefined,
          accommodationsByName: undefined,
          competitions: [],
          competitionsById: undefined,
          competitionsByName: undefined,
          distances: [],
          distancesById: undefined,
          distancesByName: undefined,
          editions: [],
          editionsById: undefined,
          editionsByYear: undefined,
          isLoading: false,
        });
        console.log({ error });
      });
  };

  const fetchSystemOptions = createDedupedFetcher(fetchSystemOptionsImpl);

  return {
    ...flattenActions([
      createTechnicalTypeCrudAction(
        set as unknown as StoreSetter<Record<string, unknown>>,
        get as unknown as StoreGetter<Record<string, unknown>>,
        DISTANCES_CRUD_CONFIG,
      ),
      createTechnicalTypeCrudAction(
        set as unknown as StoreSetter<Record<string, unknown>>,
        get as unknown as StoreGetter<Record<string, unknown>>,
        ACCOMMODATIONS_CRUD_CONFIG,
      ),
      createTechnicalTypeCrudAction(
        set as unknown as StoreSetter<Record<string, unknown>>,
        get as unknown as StoreGetter<Record<string, unknown>>,
        COMPETITIONS_CRUD_CONFIG,
      ),
    ]),
    ...createEditionsSlice(...params),
    distances: [],
    accommodations: [],
    competitions: [],
    dateFormat: {
      [LanguageKeys.ro]: 'DD.MM.YYYY.',
      [LanguageKeys.hu]: 'YYYY.MM.DD.',
      [LanguageKeys.en]: 'M/DD/YYYY',
    },
    dateTimeFormat: {
      [LanguageKeys.ro]: 'DD.MM.YYYY. HH:mm',
      [LanguageKeys.hu]: 'YYYY.MM.DD. HH:mm',
      [LanguageKeys.en]: 'M/DD/YYYY h:mm a',
    },
    roles: [],
    timeFormat: {
      [LanguageKeys.ro]: 'HH:mm',
      [LanguageKeys.hu]: 'HH:mm',
      [LanguageKeys.en]: 'h:mm a',
    },

    isLoading: false,

    fetchRoles: async () => {
      set({ isLoading: true });
      return SECURE_API.get<IRole[]>(`roles/`)
        .then(({ data }) => {
          set({
            roles: data,
            rolesById: mapKeys(data, 'id'),
            isLoading: false,
          });
        })
        .catch((error: string) => {
          set({
            roles: [],
            rolesById: undefined,
            isLoading: false,
          });
          console.log({ error });
        });
    },
    fetchSystemOptions,
    setDateTimeFormat: args => set(args),
  };
});

export default useSystemOptionsStore;
